import * as Icons from '@ant-design/icons';
import { Button, Divider, Form, Select } from 'antd';
import { Stack } from '@qcx/ui';
import { useCallback, useMemo } from 'react';
import { useCreate, useNotification } from '@refinedev/core';
import { useDisciplineSelect } from 'hooks/useDisciplineSelect';
import { useForm } from '@refinedev/antd';

// Classificação do vínculo, só na graduação. '' representa "sem classificação" no select e vai como null para a API.
export const disciplineModuleClassificationOptions = [
  { label: 'Nenhuma', value: '' },
  { label: 'Extensão', value: 'extension' },
  { label: 'Estágio', value: 'internship' },
  { label: 'Prática', value: 'practice' }
];

export const classificationFieldProps = {
  getValueProps: (value?: string | null) => ({ value: value ?? '' }),
  normalize: (value?: string) => value || null
};

interface ModuleDisciplineFormProps {
  moduleId: string;
  isUndergraduate: boolean;
  // Disciplinas já vinculadas ao módulo; saem do select para não duplicar o vínculo.
  linkedDisciplineIds: string[];
  disciplineCreateModalShow: () => void;
}

export const ModuleDisciplineForm = ({
  moduleId,
  isUndergraduate,
  linkedDisciplineIds,
  disciplineCreateModalShow
}: ModuleDisciplineFormProps) => {
  const { form } = useForm();
  const { open } = useNotification();
  const createResource = 'graduate/disciplines_modules';

  const {
    selectProps: disciplineSelectProps,
    options: disciplineOptions,
    queryResult: disciplineQueryResult
  } = useDisciplineSelect();

  const availableDisciplineOptions = useMemo(
    () =>
      disciplineOptions.filter(
        option => !linkedDisciplineIds.includes(option.value)
      ),
    [disciplineOptions, linkedDisciplineIds]
  );

  const { mutate: createDiscipline } = useCreate();
  const handleSubmit = useCallback(async () => {
    await form
      .validateFields()
      .then(() => {
        const disciplineId = form.getFieldValue('discipline_id');
        const kind = form.getFieldValue('kind');
        const classification = isUndergraduate
          ? { classification: form.getFieldValue('classification') ?? null }
          : {};

        createDiscipline(
          {
            resource: createResource,
            values: {
              kind,
              ...classification,
              discipline_id: disciplineId,
              module_id: moduleId
            },
            successNotification: () => {
              return {
                description: 'Sucesso!',
                message: 'Disciplina vinculada ao módulo com sucesso!',
                type: 'success'
              };
            }
          },
          {
            onSuccess: () => {
              form.resetFields();
            }
          }
        );
      })
      .catch(() => {
        open?.({
          type: 'error',
          message: `Favor conferir todos os campos obrigatórios.`,
          description: 'Error ao criar registro!'
        });
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, disciplineQueryResult, moduleId, isUndergraduate]);

  return (
    <Form form={form} key={`form-${moduleId}`}>
      <Stack css={{ gap: '$2', justifyContent: 'space-between' }}>
        <Form.Item
          style={{ flex: 1 }}
          label="Disciplina"
          name="discipline_id"
          rules={[{ required: true, message: 'Disciplina é obrigatória' }]}
        >
          <Select
            placeholder="Selecione a disciplina ..."
            {...disciplineSelectProps}
            options={availableDisciplineOptions}
            style={{ minWidth: 200 }}
            dropdownRender={menu => (
              <>
                {menu}
                <Divider style={{ margin: '4px 0' }} />
                <Button
                  className="w-full !text-left"
                  type="primary"
                  ghost
                  onClick={disciplineCreateModalShow}
                  icon={<Icons.PlusOutlined />}
                >
                  Nova disciplina
                </Button>
              </>
            )}
          />
        </Form.Item>
        <Form.Item
          label="Tipo"
          name="kind"
          initialValue="mandatory"
          rules={[{ required: true, message: 'Tipo é obrigatório' }]}
        >
          <Select
            placeholder="Selecione o tipo ..."
            options={[
              { label: 'Obrigatória', value: 'mandatory' },
              { label: 'Optativa', value: 'optional' }
            ]}
            style={{ minWidth: 200 }}
            defaultValue={'mandatory'}
          />
        </Form.Item>
        {isUndergraduate && (
          <Form.Item
            label="Classificação"
            name="classification"
            initialValue={null}
            {...classificationFieldProps}
          >
            <Select
              options={disciplineModuleClassificationOptions}
              style={{ minWidth: 160 }}
            />
          </Form.Item>
        )}
        <Button onClick={handleSubmit}>Inserir</Button>
      </Stack>
    </Form>
  );
};
